import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';
import Grid from '@material-ui/core/Grid';
import { LocalizationProvider, MobileDatePicker, MobileTimePicker } from '@material-ui/pickers';
import DayJsAdapter from '@material-ui/pickers/adapter/dayjs';
import { useFormContext } from '../Form';
import 'dayjs/locale/nl';
import ErrorMessage from '../ErrorMessage';
import useRegisterInput from '../Form/useRegisterInput';
import TextField from '../TextField';
import translations from './translations';
import useIntl from '@afosto/utils-react/intl/useIntl';
import styled from 'styled-components';
import isEqual from 'lodash.isequal';
import dayjs from 'dayjs';
import isDefined from '../../../afosto-utils/src/isDefined';

const StyledInputLabel = styled(InputLabel)`
  && {
    margin-top: 16px;
    margin-bottom: -20px;
  }
`;

const ForwardTextField = React.forwardRef((props, ref) => <TextField inputRef={ref} {...props} />);

const DateTimeGroup = ({
  name,
  inline,
  isEditing,
  onChange,
  onEdit,
  label,
  dateLabel,
  timeLabel,
  value,
  style,
  variant,
  disableFuture,
  disablePast,
  readOnly,
  error,
  errorMessage,
  helperText,
  register: shouldRegister,
  inputVariant,
  fullWidth,
  margin,
  initTimeAsCurrentTime,
  ...props
}) => {
  useRegisterInput(name, shouldRegister);
  const [dateValue, setDateValue] = useState(null);
  const [timeValue, setTimeValue] = useState(null);

  const intl = useIntl();
  const formContext = useFormContext();
  const {
    editInline: formInline = false,
    isEditing: formIsEditing = true,
    errors = {},
    setValue,
    watch,
  } = formContext || {};
  const parsedInputProps = {
    inline: inline ?? formInline,
    isEditing: isEditing ?? formIsEditing,
    error: error ?? !!errors[name],
    value: value ?? (watch ? watch(name) : null),
  };
  const inputProps = { ...props, readOnly };
  const isInline = parsedInputProps.inline && !parsedInputProps.isEditing;

  if (isInline || readOnly) {
    parsedInputProps.disabled = true;
    parsedInputProps.InputLabelProps = {
      ...(parsedInputProps.InputLabelProps || {}),
      shrink: true,
    };
  }

  const valueRef = useRef();
  const providedValue = parsedInputProps?.value;
  const valueIsDifferent =
    (isDefined(valueRef.current) || isDefined(providedValue)) &&
    !isEqual(valueRef.current, providedValue);

  /**
   * Handle the change on the select.
   * @param {string|date} date
   * @param {string|date} time
   */
  const handleChange = (date, time) => {
    const newDate = !date || !date.isValid() ? null : new Date(date);
    const newTime = !time || !time.isValid() ? null : new Date(time);
    const newValue = newDate;

    if (newDate !== null && newTime !== null) {
      newValue.setHours(newTime.getHours());
      newValue.setMinutes(newTime.getMinutes());
      newValue.setSeconds(0);
      newValue.setMilliseconds(0);
    } else if (newDate !== null && !initTimeAsCurrentTime) {
      newValue.setHours(0);
      newValue.setMinutes(0);
      newValue.setSeconds(0);
      newValue.setMilliseconds(0);
    }

    if (formContext && !onChange) {
      setValue(name, newValue);
      return;
    }

    if (onChange) {
      onChange(newValue);
    }
  };

  const handleOnChange = type => date => {
    if (type === 'date') {
      setDateValue(date);
      handleChange(date, timeValue);
    }
    if (type === 'time') {
      setTimeValue(date);
      handleChange(dateValue, date);
    }
  };

  /**
   * Return the error message.
   */
  const getErrorMessage = () => {
    if (formContext && !errorMessage) {
      return errors[name] && <ErrorMessage errors={errors} name={name} />;
    }

    return errorMessage;
  };

  useEffect(() => {
    const handleNewValue = newValue => {
      if (newValue.isValid()) {
        setTimeValue(newValue);
      }
      setDateValue(newValue);
    };

    if (valueIsDifferent) {
      handleNewValue(dayjs(providedValue));
    }
  }, [valueIsDifferent, providedValue]);

  useEffect(() => {
    valueRef.current = parsedInputProps.value;
  });

  return (
    <LocalizationProvider dateAdapter={DayJsAdapter} locale="nl">
      <StyledInputLabel shrink variant="outlined">
        {label}
      </StyledInputLabel>
      <Grid container spacing={1}>
        <Grid item xs={8}>
          <MobileDatePicker
            {...inputProps}
            autoOk
            renderInput={renderInputProps => (
              <ForwardTextField
                {...renderInputProps}
                variant={inputVariant}
                name={name}
                helperText={parsedInputProps.error ? getErrorMessage() : helperText}
                fullWidth={fullWidth}
                error={parsedInputProps.error}
                InputLabelProps={parsedInputProps.InputLabelProps}
                inline={inline}
                readOnly={readOnly}
                isEditing={isEditing}
                placeholder={isInline ? '' : intl.formatMessage(translations.datePlaceholder)}
                margin={margin}
                onEdit={onEdit}
              />
            )}
            disabled={parsedInputProps.disabled}
            inputFormat="DD/MM/YYYY"
            disableFuture={disableFuture}
            disablePast={disablePast}
            ampm={false}
            displayStaticWrapperAs={variant}
            value={dateValue}
            InputAdornmentProps={{ position: 'start' }}
            onChange={handleOnChange('date')}
            style={style}
            cancelLabel={intl.formatMessage(translations.cancelLabel)}
            clearLabel={intl.formatMessage(translations.clearLabel)}
            okLabel={intl.formatMessage(translations.okLabel)}
            todayLabel={intl.formatMessage(translations.todayLabel)}
            toolbarTitle={intl.formatMessage(translations.toolbarTitle)}
          />
        </Grid>
        <Grid item xs={4}>
          <MobileTimePicker
            {...inputProps}
            autoOk
            renderInput={renderInputProps => (
              <ForwardTextField
                {...renderInputProps}
                variant={inputVariant}
                name={name}
                helperText={parsedInputProps.error ? getErrorMessage() : helperText}
                fullWidth={fullWidth}
                error={parsedInputProps.error}
                InputLabelProps={parsedInputProps.InputLabelProps}
                inline={inline}
                readOnly={readOnly}
                isEditing={isEditing}
                placeholder={isInline ? '' : intl.formatMessage(translations.timePlaceholder)}
                margin={margin}
                onEdit={onEdit}
              />
            )}
            disabled={parsedInputProps.disabled}
            inputFormat="HH:mm"
            disableFuture={disableFuture}
            disablePast={disablePast}
            ampm={false}
            displayStaticWrapperAs={variant}
            value={timeValue}
            InputAdornmentProps={{ position: 'start' }}
            onChange={handleOnChange('time')}
            style={style}
            cancelLabel={intl.formatMessage(translations.cancelLabel)}
            clearLabel={intl.formatMessage(translations.clearLabel)}
            okLabel={intl.formatMessage(translations.okLabel)}
            todayLabel={intl.formatMessage(translations.todayLabel)}
            toolbarTitle={intl.formatMessage(translations.toolbarTitle)}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

DateTimeGroup.propTypes = {
  name: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  label: PropTypes.string,
  dateLabel: PropTypes.string,
  timeLabel: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  style: PropTypes.object,
  variant: PropTypes.oneOf(['desktop', 'mobile', 'static']),
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  helperText: PropTypes.string,
  register: PropTypes.bool,
  inputVariant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  fullWidth: PropTypes.bool,
  margin: PropTypes.oneOf(['normal', 'dense', 'none']),
  initTimeAsCurrentTime: PropTypes.bool,
};

DateTimeGroup.defaultProps = {
  inline: undefined,
  isEditing: undefined,
  onEdit: undefined,
  label: '',
  dateLabel: '',
  timeLabel: '',
  value: undefined,
  onChange: undefined,
  style: undefined,
  variant: 'static',
  inputVariant: 'outlined',
  disableFuture: true,
  disablePast: false,
  readOnly: false,
  error: undefined,
  errorMessage: undefined,
  helperText: undefined,
  register: true,
  fullWidth: true,
  margin: 'normal',
  initTimeAsCurrentTime: true,
};

export default DateTimeGroup;
